import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ImageKind =
  | 'bitewing'
  | 'periapical'
  | 'panorama'
  | 'cbct'
  | 'intraoral_photo'
  | 'tooth_chart_snapshot'
  | 'extraoral_photo';

export interface ImageAdjustments {
  brightness?: number;   // 0..2, default 1
  contrast?: number;     // 0..2, default 1
  gamma?: number;        // 0..2, default 1
  sharpen?: number;      // 0..1, default 0
  invert?: boolean;
  rotation_deg?: 0 | 90 | 180 | 270;
}

export interface ImageAnnotation {
  id: string;
  kind: 'line' | 'circle' | 'rect' | 'arrow' | 'freehand' | 'text';
  points: number[];
  color: string;
  label?: string;
  created_by: string;
  created_at: string;
}

export interface ImageMeasurement {
  id: string;
  kind: 'length' | 'angle';
  points: number[];
  value_px: number;
  value_mm: number | null;
  calibrated: boolean;
  label?: string;
}

export interface OverlayTransform {
  x: number;
  y: number;
  scale: number;
  rotation: number;
  opacity: number;
}

export interface ToothImageRow {
  id: string;
  clinic_id: string;
  patient_id: string;
  document_id: string;
  tooth_fdis: string[];
  image_kind: ImageKind;
  acquired_at: string;
  acquired_by: string | null;
  notes: string | null;
  overlay_transform: OverlayTransform | null;
  series_id: string | null;
  series_position: number | null;
  adjustments: ImageAdjustments;
  annotations: ImageAnnotation[];
  measurements: ImageMeasurement[];
  created_at: string;
  updated_at: string;
}

export interface EnrichedToothImage extends ToothImageRow {
  signedUrl: string | null;
  document_filename: string | null;
  document_original_filename: string | null;
  document_mime_type: string | null;
  document_size_bytes: number | null;
  document_storage_path: string | null;
}

// ── Query Keys ─────────────────────────────────────────────────────────────────

export const toothImagesKey = (patientId: string | null) =>
  ['dental', 'tooth-images', patientId] as const;

// ── Helpers ────────────────────────────────────────────────────────────────────

const SIGNED_URL_EXPIRY = 3600; // 1 hour

async function enrichWithSignedUrls(
  rows: (ToothImageRow & {
    document_filename?: string | null;
    document_original_filename?: string | null;
    document_mime_type?: string | null;
    document_size_bytes?: number | null;
    document_storage_path?: string | null;
  })[],
): Promise<EnrichedToothImage[]> {
  return Promise.all(
    rows.map(async (row) => {
      const storagePath = row.document_storage_path ?? null;
      let signedUrl: string | null = null;

      if (storagePath) {
        try {
          const { data } = await supabase.storage
            .from('patient-documents')
            .createSignedUrl(storagePath, SIGNED_URL_EXPIRY);
          signedUrl = data?.signedUrl ?? null;
        } catch {
          // Non-critical — image tile will show broken state
        }
      }

      return {
        ...row,
        signedUrl,
        document_filename: row.document_filename ?? null,
        document_original_filename: row.document_original_filename ?? null,
        document_mime_type: row.document_mime_type ?? null,
        document_size_bytes: row.document_size_bytes ?? null,
        document_storage_path: storagePath,
      };
    }),
  );
}

// ── Queries ────────────────────────────────────────────────────────────────────

/**
 * Fetches all tooth_images for a patient, joined with documents metadata.
 * Signed URLs are generated for each image (1-hour expiry).
 * Cached for 5 minutes.
 */
export function usePatientToothImages(patientId: string | null) {
  return useQuery({
    queryKey: toothImagesKey(patientId),
    enabled: Boolean(patientId),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<EnrichedToothImage[]> => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from('tooth_images')
        .select(
          `id, clinic_id, patient_id, document_id, tooth_fdis, image_kind,
           acquired_at, acquired_by, notes, overlay_transform,
           series_id, series_position, adjustments, annotations, measurements,
           created_at, updated_at,
           documents!document_id(filename, original_filename, mime_type, size_bytes, storage_path)`,
        )
        .eq('patient_id', patientId)
        .order('acquired_at', { ascending: false });

      if (error) throw error;

      const normalized = (data ?? []).map((row) => {
        const doc = (row as unknown as { documents: Record<string, unknown> | null }).documents;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          document_id: row.document_id,
          tooth_fdis: (row.tooth_fdis ?? []) as string[],
          image_kind: row.image_kind as ImageKind,
          acquired_at: row.acquired_at,
          acquired_by: row.acquired_by,
          notes: row.notes,
          overlay_transform: row.overlay_transform as OverlayTransform | null,
          series_id: row.series_id,
          series_position: row.series_position,
          adjustments: (row.adjustments ?? {}) as unknown as ImageAdjustments,
          annotations: (row.annotations ?? []) as unknown as ImageAnnotation[],
          measurements: (row.measurements ?? []) as unknown as ImageMeasurement[],
          created_at: row.created_at,
          updated_at: row.updated_at,
          document_filename: (doc?.filename as string) ?? null,
          document_original_filename: (doc?.original_filename as string) ?? null,
          document_mime_type: (doc?.mime_type as string) ?? null,
          document_size_bytes: (doc?.size_bytes as number) ?? null,
          document_storage_path: (doc?.storage_path as string) ?? null,
        };
      });

      return enrichWithSignedUrls(normalized);
    },
  });
}

/**
 * Derived filter — returns only images covering a specific FDI tooth code.
 */
export function useToothImagesForTooth(
  patientId: string | null,
  toothFdi: string | null,
): EnrichedToothImage[] {
  const { data } = usePatientToothImages(patientId);
  if (!data || !toothFdi) return [];
  return data.filter((img) => img.tooth_fdis.includes(toothFdi));
}

// ── Upload mutation ────────────────────────────────────────────────────────────

export interface UploadToothImageParams {
  file: File;
  patientId: string;
  toothFdis: string[];
  imageKind: ImageKind;
  notes?: string;
  seriesId?: string;
  seriesPosition?: number;
  onProgress?: (percent: number) => void;
}

export function useUploadToothImage(clinicId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      patientId,
      toothFdis,
      imageKind,
      notes,
      seriesId,
      seriesPosition,
      onProgress,
    }: UploadToothImageParams): Promise<ToothImageRow> => {
      onProgress?.(5);

      // Build storage path matching existing pattern
      const docSegment = crypto.randomUUID();
      const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${clinicId}/${patientId}/${docSegment}/${safeFilename}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('patient-documents')
        .upload(storagePath, file, { upsert: false });

      if (uploadError) throw uploadError;
      onProgress?.(50);

      // Register document
      const { data: docData, error: docError } = await supabase.rpc('register_document', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
        p_filename: safeFilename,
        p_original_filename: file.name,
        p_mime_type: file.type,
        p_size_bytes: file.size,
        p_storage_path: storagePath,
        p_tags: ['dental-image', imageKind],
        p_notes: notes,
      });

      if (docError) throw docError;
      onProgress?.(75);

      const documentId = (docData as unknown as { id: string }).id;

      // Link to tooth via tooth_images
      const { data: imgData, error: imgError } = await supabase.rpc('upsert_tooth_image', {
        p_patient_id: patientId,
        p_document_id: documentId,
        p_tooth_fdis: toothFdis,
        p_image_kind: imageKind,
        p_notes: notes ?? undefined,
        p_series_id: seriesId ?? undefined,
        p_series_position: seriesPosition ?? undefined,
      });

      if (imgError) throw imgError;
      onProgress?.(100);

      return imgData as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
      void qc.invalidateQueries({ queryKey: ['dental', 'tooth-badges', patientId] });
    },
  });
}

// ── Delete mutation ────────────────────────────────────────────────────────────

export function useDeleteToothImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      patientId,
    }: {
      imageId: string;
      patientId: string;
    }): Promise<void> => {
      const { error } = await supabase.rpc('delete_tooth_image', { p_id: imageId });
      if (error) throw error;
      void patientId;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
      void qc.invalidateQueries({ queryKey: ['dental', 'tooth-badges', patientId] });
    },
  });
}

// ── Update meta mutation ───────────────────────────────────────────────────────

export function useUpdateToothImageMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      toothFdis,
      imageKind,
      notes,
    }: {
      imageId: string;
      patientId: string;
      toothFdis?: string[];
      imageKind?: ImageKind;
      notes?: string | null;
    }): Promise<ToothImageRow> => {
      const { data, error } = await supabase.rpc('upsert_tooth_image', {
        p_id: imageId,
        p_tooth_fdis: toothFdis,
        p_image_kind: imageKind,
        p_notes: notes ?? undefined,
      });
      if (error) throw error;
      return data as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
    },
  });
}

// ── Update overlay mutation ────────────────────────────────────────────────────

export function useUpdateToothImageOverlay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      overlayTransform,
    }: {
      imageId: string;
      patientId: string;
      overlayTransform: OverlayTransform;
    }): Promise<ToothImageRow> => {
      const { data, error } = await supabase.rpc('update_tooth_image_overlay', {
        p_id: imageId,
        p_overlay_transform: overlayTransform as unknown as never,
      });
      if (error) throw error;
      return data as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
    },
  });
}

// ── Update adjustments mutation ────────────────────────────────────────────────

export function useUpdateToothImageAdjustments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      adjustments,
    }: {
      imageId: string;
      patientId: string;
      adjustments: ImageAdjustments;
    }): Promise<ToothImageRow> => {
      const { data, error } = await supabase.rpc('update_tooth_image_adjustments', {
        p_id: imageId,
        p_adjustments: adjustments as unknown as never,
      });
      if (error) throw error;
      return data as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
    },
  });
}

// ── Update annotations mutation ────────────────────────────────────────────────

export function useUpdateToothImageAnnotations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      annotations,
    }: {
      imageId: string;
      patientId: string;
      annotations: ImageAnnotation[];
    }): Promise<ToothImageRow> => {
      const { data, error } = await supabase.rpc('update_tooth_image_annotations', {
        p_id: imageId,
        p_annotations: annotations as unknown as never,
      });
      if (error) throw error;
      return data as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
    },
  });
}

// ── Update measurements mutation ───────────────────────────────────────────────

export function useUpdateToothImageMeasurements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      imageId,
      measurements,
    }: {
      imageId: string;
      patientId: string;
      measurements: ImageMeasurement[];
    }): Promise<ToothImageRow> => {
      const { data, error } = await supabase.rpc('update_tooth_image_measurements', {
        p_id: imageId,
        p_measurements: measurements as unknown as never,
      });
      if (error) throw error;
      return data as unknown as ToothImageRow;
    },
    onSuccess: (_data, { patientId }) => {
      void qc.invalidateQueries({ queryKey: toothImagesKey(patientId) });
    },
  });
}

// ── Helpers for image-kind labels (English used internally, i18n at render) ────

export const IMAGE_KINDS: ImageKind[] = [
  'bitewing',
  'periapical',
  'panorama',
  'cbct',
  'intraoral_photo',
  'tooth_chart_snapshot',
  'extraoral_photo',
];

/** Build CSS filter string from adjustments object. */
export function buildCssFilter(adj: ImageAdjustments): string {
  const parts: string[] = [];
  if (adj.brightness !== undefined) parts.push(`brightness(${adj.brightness})`);
  if (adj.contrast !== undefined) parts.push(`contrast(${adj.contrast})`);
  if (adj.invert) parts.push('invert(1)');
  return parts.join(' ');
}

/** Build CSS transform string from adjustments (rotation) + optional zoom/translate. */
export function buildCssTransform(
  adj: ImageAdjustments,
  zoom?: number,
  panX?: number,
  panY?: number,
): string {
  const parts: string[] = [];
  if (adj.rotation_deg) parts.push(`rotate(${adj.rotation_deg}deg)`);
  if (zoom && zoom !== 1) parts.push(`scale(${zoom})`);
  if (panX !== undefined && panY !== undefined) parts.push(`translate(${panX}px, ${panY}px)`);
  return parts.join(' ') || 'none';
}
